import React from 'react';
import { useQuery, QueryHookOptions } from 'react-apollo';
import { SurveyThemeProvider } from './context';
import getSurveyDesign from './SurveyTheme.graphql';
import { formatTheme } from './helpers';
import { GetSurveyDesignResponse } from './graphQlApiResponse.types';
import { SurveyTheme as SurveyThemeType } from './types';

type Props = {
  surveyId: string;
  targetEndPoint?: string;
  headers?: Record<string, unknown>;
  children: React.ReactNode;
};

/* A simple wrapper composes inner children with the fetched theming info. */
const SurveyTheme = ({
  children,
  surveyId,
  targetEndPoint,
  headers,
}: Props): JSX.Element | null => {
  const queryOptions: QueryHookOptions<
    GetSurveyDesignResponse,
    { id: string }
  > = {
    variables: {
      id: surveyId,
    },
    // acquire data even when an error occurs
    errorPolicy: 'all',
    partialRefetch: true,
  };

  // enable query context if tgtEndPoint provided
  if (targetEndPoint) {
    queryOptions.context = {};
    queryOptions.context.targetPath = `/lo-graphql/${targetEndPoint}`;
    // include headers if provided
    if (headers) {
      queryOptions.context.headers = headers;
    }
  }

  const { data, loading, error: fetchError, refetch } = useQuery(
    getSurveyDesign,
    queryOptions
  );

  /* No-op on the loading state.
   * The wrapping component should know better how to compose with a representable skeleton.
   */
  if (loading) {
    return null;
  }

  const surveyTheme: SurveyThemeType = formatTheme(data);

  return (
    <SurveyThemeProvider
      theme={{
        ...surveyTheme,
        ...(fetchError && { refetch, fetchError }),
      }}
    >
      {children}
    </SurveyThemeProvider>
  );
};

export default SurveyTheme;
